import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import api from "../../api";
import { useTranslation } from "react-i18next";
import { PlusCircle, MinusCircle } from "lucide-react";

const sumSalaries = (employees = []) =>
  employees.reduce((sum, e) => sum + (parseFloat(e.salary) || 0), 0);

const getInitialCharges = (calculationData) => {
  if (!calculationData) return "";
  const usure = (calculationData.distanceKm || 0) * 0.4;
  const carburant = parseFloat(calculationData.fuelCost || 0);
  const autres =
    Array.isArray(calculationData.otherCharges)
      ? calculationData.otherCharges.reduce(
          (sum, c) => sum + (parseFloat(c.amount) || 0),
          0
        )
      : 0;
  return (usure + carburant + autres).toFixed(2);
};

// CP depuis le logistics du devis : CP explicite, sinon extrait de ville/adresse.
const zipFrom = (logistics, prefix) => {
  if (!logistics) return "";
  const explicit = String(logistics[`${prefix}_zip`] || "").trim();
  if (/^\d{5}$/.test(explicit)) return explicit;
  const src = `${logistics[`${prefix}_city`] || ""} ${logistics[`${prefix}_address`] || ""}`;
  return src.match(/\b(\d{5})\b/)?.[1] || "";
};

const getInitialEmployees = (init) => {
  if (
    init &&
    init.calculationData &&
    Array.isArray(init.calculationData.salaries) &&
    init.calculationData.salaries.length
  ) {
    return init.calculationData.salaries.map((s) => ({
      employee_name: s.name || "",
      salary: s.salary || "",
    }));
  }
  if (init && Array.isArray(init.employees) && init.employees.length) {
    return init.employees;
  }
  return [{ employee_name: "", salary: "" }];
};

const MissionCreateModal = ({
  isOpen,
  onClose,
  initialData = {},
  onSuccess,
  quoteId,
}) => {
  const { t } = useTranslation();

  const [type, setType] = useState(initialData.type || "Moving");
  const [client, setClient] = useState(initialData.client || "");
  const [address, setAddress] = useState(initialData.address || "");
  const [date, setDate] = useState(initialData.date || "");
  const [priceHT, setPriceHT] = useState(initialData.priceHT || "");
  const [charges, setCharges] = useState(getInitialCharges(initialData.calculationData));
  const [employees, setEmployees] = useState(getInitialEmployees(initialData));
  const [departureZip, setDepartureZip] = useState(zipFrom(initialData.logistics, "loading"));
  const [arrivalZip, setArrivalZip] = useState(zipFrom(initialData.logistics, "delivery"));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setType(initialData.type || "Moving");
    setClient(initialData.client || "");
    setAddress(initialData.address || "");
    setDate(initialData.date || "");
    setPriceHT(initialData.priceHT || "");
    setCharges(getInitialCharges(initialData.calculationData));
    setEmployees(getInitialEmployees(initialData));
    setDepartureZip(zipFrom(initialData.logistics, "loading"));
    setArrivalZip(zipFrom(initialData.logistics, "delivery"));
  }, [initialData]);

  const totalSalary = sumSalaries(employees);
  const priceTTC = priceHT ? (parseFloat(priceHT) * 1.2).toFixed(2) : "0.00";

  const handleAddEmployee = () => {
    setEmployees([...employees, { employee_name: "", salary: "" }]);
  };
  const handleRemoveEmployee = (index) => {
    const updated = [...employees];
    updated.splice(index, 1);
    setEmployees(updated.length ? updated : [{ employee_name: "", salary: "" }]);
  };
  const handleEmployeeChange = (index, field, value) => {
    const updated = [...employees];
    updated[index][field] = value;
    setEmployees(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Déménagement : code postal d'arrivée obligatoire.
    if (type === "Moving" && !/^\d{5}$/.test(String(arrivalZip).trim())) {
      toast.error(t("mission.arrivalZipRequired", "Code postal d'arrivée requis pour un déménagement."));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        type,
        client,
        address,
        date,
        priceHT: parseFloat(priceHT) || 0,
        priceTTC: parseFloat(priceTTC) || 0,
        charges: parseFloat(charges) || 0,
        employees,
        salary: totalSalary,
        departure_zip: String(departureZip).trim(),
        arrival_zip: String(arrivalZip).trim(),
        ...(quoteId ? { quote_id: quoteId } : {})
      };
      await api.post("/missions", payload);
      toast.success(t("mission.created"));
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(t("mission.createError"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm p-4 flex items-center justify-center z-50">
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl sm:max-w-2xl relative flex flex-col max-h-[90vh]"
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-10 px-6 py-4 border-b bg-white/95 backdrop-blur flex items-center justify-between">
          <h2 className="text-xl sm:text-2xl font-extrabold text-blue-700 flex items-center gap-2">
            <span role="img" aria-label="Package">📦</span> {t("mission.create")}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-red-600 text-2xl font-bold"
            title={t("buttons.cancel")}
            aria-label={t("buttons.cancel")}
          >
            ×
          </button>
        </div>

        {/* Body scrollable */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-600">{t("mission.form.type")}</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full p-2 border rounded-lg shadow-sm focus:ring focus:border-blue-400"
                required
              >
                <option value="Moving">🚚 {t("mission.form.moving")}</option>
                <option value="Delivery">📦 {t("mission.form.delivery")}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-600">{t("mission.form.date")}</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full p-2 border rounded-lg shadow-sm focus:ring focus:border-blue-400"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-600">{t("mission.form.client")}</label>
              <input
                type="text"
                value={client}
                onChange={(e) => setClient(e.target.value)}
                className="w-full p-2 border rounded-lg shadow-sm focus:ring focus:border-blue-400"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-600">{t("mission.form.address")}</label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full p-2 border rounded-lg shadow-sm focus:ring focus:border-blue-400"
            />
          </div>

          {/* Trajet : CP départ → arrivée */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-600">
                {t("mission.form.departureZip", "CP départ")}
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={departureZip}
                onChange={(e) => setDepartureZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                placeholder="75015"
                className="w-full p-2 border rounded-lg shadow-sm focus:ring focus:border-blue-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-600">
                {t("mission.form.arrivalZip", "CP arrivée")}
                {type === "Moving" && <span className="text-red-400 ml-0.5">*</span>}
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={arrivalZip}
                onChange={(e) => setArrivalZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                placeholder="37000"
                className="w-full p-2 border rounded-lg shadow-sm focus:ring focus:border-blue-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-600">{t("mission.form.priceHT")}</label>
              <input
                type="number"
                value={priceHT}
                onChange={(e) => setPriceHT(e.target.value)}
                className="w-full p-2 border rounded-lg shadow-sm focus:ring focus:border-blue-400"
                required
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-600">{t("mission.form.charges")}</label>
              <input
                type="number"
                value={charges}
                onChange={(e) => setCharges(e.target.value)}
                className="w-full p-2 border rounded-lg shadow-sm focus:ring focus:border-blue-400"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div className="flex justify-end items-end">
            <div className="text-lg font-bold bg-blue-50 px-4 py-2 rounded-xl border text-blue-800 shadow-sm">
              🧾 {t("mission.form.priceTTC")}: <span className="ml-2">{priceTTC} €</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-600">{t("mission.form.employees")}</label>
            <div className="space-y-2">
              {employees.map((emp, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-2 items-center bg-gray-50 rounded-lg p-2"
                >
                  <input
                    type="text"
                    placeholder={t("mission.form.employeeName")}
                    value={emp.employee_name}
                    onChange={(e) => handleEmployeeChange(idx, "employee_name", e.target.value)}
                    className="col-span-5 p-2 border rounded-lg shadow-sm"
                  />
                  <input
                    type="number"
                    placeholder={t("mission.form.salary")}
                    value={emp.salary}
                    onChange={(e) => handleEmployeeChange(idx, "salary", e.target.value)}
                    className="col-span-5 p-2 border rounded-lg shadow-sm"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveEmployee(idx)}
                    className="col-span-2 flex items-center justify-center text-red-600 hover:text-red-800 font-semibold"
                    title={t("mission.form.remove")}
                  >
                    <MinusCircle size={22} />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={handleAddEmployee}
              className="mt-2 flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-xl shadow"
            >
              <PlusCircle size={20} /> {t("mission.form.addEmployee")}
            </button>
          </div>
        </form>

        {/* Footer sticky */}
        <div className="sticky bottom-0 z-10 px-6 py-3 border-t bg-white/95 backdrop-blur flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-100 transition"
          >
            {t("buttons.cancel")}
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={(e) => {
              const container = e.currentTarget.closest('[role="dialog"]');
              const form = container?.querySelector("form");
              form?.requestSubmit();
            }}
            className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2 rounded-lg font-semibold shadow"
          >
            {submitting ? "⏳ …" : `💾 ${t("buttons.save")}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MissionCreateModal;
